import {useReducer, useCallback} from 'react';
import {AIFilterSearchContextType, defaultAIFilterSearchContext} from '../AIFilterSearch.context';
import {getAISuggestedFilters} from '../../../../../../../../services/rest/llm/aiSuggestedFilters';
import {getPossibleSearchValues} from '../../../../../../../../_legacy/Searches/components/AISearch/utils';
import {Column} from '../../../../../../../../stores/Form/Column';
import {getColumnTypeKey} from '../../../../../../../../utils/columns';
import {setAIFilterValues} from '../../../utils';
import {PatchFilter} from '../../../index';

function reducer(
    state: AIFilterSearchContextType,
    action: {type: string; payload: Partial<AIFilterSearchContextType>}
) {
    return {...state, ...action.payload};
}

type Props = {
    formTitle: string;
    columns: Column[];
    clearFilters: () => void;
    setSearchFilters: (filters: PatchFilter[]) => void;
    isVisibleByDefault?: boolean;
};

export const useAIFilterSearch = (props: Props) => {
    const {formTitle, columns, clearFilters, setSearchFilters, isVisibleByDefault = false} = props;
    const [state, dispatch] = useReducer(reducer, {
        ...defaultAIFilterSearchContext,
        aiSearchVisible: isVisibleByDefault
    });

    const handleAiSearch = useCallback(
        async (value: string) => {
            dispatch({
                type: 'UPDATE_STATE',
                payload: {isLoading: true, hasError: false, isEmptyResult: false}
            });

            try {
                clearFilters();

                const res = await getAISuggestedFilters({
                    formName: formTitle,
                    query: value,
                    columns: columns.map(c => ({
                        name: c.Name,
                        title: c.Title,
                        type: getColumnTypeKey(c.Type),
                        possibleValues: getPossibleSearchValues(c)
                    }))
                });

                if (res?.filters?.length) {
                    await setAIFilterValues(setSearchFilters, columns, res.filters);
                } else {
                    if (res?.code === 7 || res?.code === 2) {
                        clearFilters();
                        dispatch({
                            type: 'UPDATE_STATE',
                            payload: {isEmptyResult: true, hasError: true, isLoading: false}
                        });
                    }
                }
            } catch (e) {
                clearFilters();
                dispatch({
                    type: 'UPDATE_STATE',
                    payload: {isEmptyResult: true, hasError: true}
                });
                console.error(e);
            } finally {
                dispatch({
                    type: 'UPDATE_STATE',
                    payload: {isLoading: false}
                });
            }
        },
        [formTitle, clearFilters, columns, setSearchFilters]
    );

    const showAISearch = useCallback(() => {
        dispatch({
            type: 'UPDATE_STATE',
            payload: {aiSearchVisible: true}
        });
    }, []);

    const resetError = useCallback(() => {
        dispatch({
            type: 'UPDATE_STATE',
            payload: {hasError: false, isEmptyResult: false}
        });
    }, []);

    return {
        state,
        handlers: {
            handleAiSearch,
            showAISearch,
            resetError
        }
    };
};
